import {useSelector} from "react-redux";
import {RootState} from "../store/rootReducer";
// CASL 7 renamed PureAbility to Ability, and the class that used to be called Ability — the one
// carrying the MongoDB conditions matcher — now comes from createMongoAbility. So the Ability
// imported here is not the Ability this file imported before: it is the plain one, with no
// conditions matcher, which is what these rules want since none of them carries a condition.
import {Ability, AbilityBuilder, AbilityTuple} from '@casl/ability';

export const SEARCH = "search";
export const EDIT_FAVORITES = "editFavorites";
export const EDIT_COMPANY_FAVORITES = "editCompanyFavorites";
export const EDIT_ITEM = "editItem";
export const SEARCH_HISTORY = "searchHistory";
export const EXTENSION_RUN = "extensionRun";
export const EXTENSION_LOAD = "extensionLoad"; // This one is only used internally for permissions on if the user can load any extensions
export const EXTENSION_MODIFY = "extensionModify"; // This one is only used internally for permissions on if the user can modify any extensions
export const EXTENSION_LOGS = "extensionLogs"; //This one is only used internally for permissions on if the user can view any extension logs
export const ORG_EXTENSION_LOAD = "orgExtensionLoad";
export const ORG_EXTENSION_MODIFY = "orgExtensionModify";
export const ORG_EXTENSION_LOGS = "orgExtensionLogs";
export const USER_EXTENSION = "userExtension";
export const CATALOG_MODIFY = "catalogModify";
export const CATALOG_BULK_LOAD = "catalogBulkLoad";
export const USER_LIST = "userList";
export const USER_MODIFY = "userModify";
export const USER_DETAILS = "userDetails";
export const ROLE_MODIFY = "roleModify";
export const ROLE_ASSIGN = "roleAssign";
export const ORG_REPORTING = "orgReporting";
export const ORG_MODIFY = "orgModify";
export const API_KEYS_ADMIN = "apiKeysAdmin";
export const API_KEYS_SELF = "apiKeysSelf";
export const TAX_CODE_MODIFY = "taxCodeModify";
export const LLM_ANALYSIS = "llmAnalysis";
export const DOCUMENT_ANALYSIS = "documentAnalysis";
export const CUSTOMER_REQUEST_ANALYSIS = "customerRequestAnalysis";
export const PROCUREMENT = "procurement";
export const STOREFRONT_READ = "storefrontRead";
export const STOREFRONT_ADMIN = "storefrontAdmin";
export const PRODUCT_PROMOS_ADMIN = "productPromosAdmin";
export const STOREFRONT_TEMPLATES = "storefrontTemplates";
export const STOREFRONT_TEMPLATES_GLOBAL = "storefrontTemplatesGlobal";
// Authoring extension folios. FOLIO_AUTHOR covers folios this organization owns; FOLIO_GLOBAL_ADMIN
// covers the global catalog and implies the former. Both are entitlement-gated server side, and the
// server re-checks ownership per record -- these only decide what the UI offers.
export const FOLIO_AUTHOR = "folioAuthor";
export const FOLIO_GLOBAL_ADMIN = "folioGlobalAdmin";
export const SMTP_ADMIN = "smtpAdmin";
// One shared gate for both product-page recommendation rows (frequently added together + more like this).
export const PRODUCT_RECOMMENDATIONS_ADMIN = "productRecommendationsAdmin";
export const AI_RESTYLE = "aiRestyle";
export const SAVED_SEARCH_READ = "savedSearchRead"
export const SAVED_SEARCH_ADMIN = "savedSearchAdmin"
export const REPORT_READ = "reportsRead"
export const REPORT_GENERATE = "reportsGenerate"



// Every rule here is a plain (action, subject) pair — "enact" against a claim name. Spelling that
// out keeps the builder and the instance on the same tuple: left to infer, the builder widens to
// CASL's full Abilities union, which also covers subject-less claim rules, and then its rules no
// longer fit update() and can() drops to a single argument.
type ClaimAbility = Ability<AbilityTuple>;

const ability: ClaimAbility = new Ability();
/** Whether the signed-in user holds a claim. A hook (it reads the claims from the store), so call it unconditionally. */
export default function useCan(action: string) {
    ability.update(useClaimRules())
    return ability.can('enact', action);
}

export function useClaimRules() {
    const {can, rules} = new AbilityBuilder<ClaimAbility>(Ability);
    const permissions = useSelector((state: RootState) => state.settings.effectiveClaims);

    if(permissions) {
        permissions.forEach( function(permission) {can('enact', permission)});
    }

    return rules;
}
